import { PageAnchor } from 'src/app/AppAuth/PageAnchor'
import styled from 'styled-components/macro'
import themeGet from '@styled-system/theme-get'
import { compose, grid, space, color } from 'styled-system'
import { TrusteroLogoFullSVG } from 'src/components/Icons/Brand'
import palette from 'src/designSystem/variables/palette'
import {
  StandardButton,
  StandardButtonSize,
  StandardButtonVariant,
} from 'src/components/Reusable/Buttons'
import backgroundSVG from 'src/components/Icons/Brand/background.svg'
import { StartFlow } from './EmailLogin/EmailLogin.styles'

// The `isInApp` flag is needed when we access the /login route from within the AppProvider
// This is needed to avoid some whitespace when seeing the OIDC providers
export const LoginPageContainer = styled.div`
  background: rgb(29, 37, 46);
  background: linear-gradient(
    146deg,
    rgba(29, 37, 46, 1) 0%,
    rgba(29, 37, 46, 1) 48%,
    rgba(162, 165, 168, 1) 100%
  );
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 1.7rem;
  justify-content: center;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  overflow: auto;
  height: 100vh;
`

export const LoginPageBackground = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: ${themeGet('colors.text.default')};

  .top-right-bubble {
    position: absolute;
    top: 0;
    right: 0;
  }
  .bottom-right-bubble {
    position: absolute;
    bottom: 0;
    right: 0;
  }
  .bottom-left-bubble {
    position: absolute;
    bottom: 0;
    left: 0;
  }
`

export const LoginPageGrid = styled.div.attrs({
  gridTemplateColumns: 'repeat(12, 1fr)',
  gridColumnGap: '24px',
  px: 'm',
})`
  ${compose(grid, space)}
  display: grid;
  z-index: 1;
  width: 100%;
  height: 100%;
  max-width: 1200px;
`

export const TrusteroLine = styled.div`
  height: 0.3rem;
  margin: 1.7rem 0;
  background: #00c9a7;
  background: linear-gradient(91deg, #00c9a7 0%, #f567a8 100%);
`

export const LineContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${palette.neutral.white};
  font-size: 1rem;
  font-weight: 400;
  margin: 1.7rem 0;
  ::before {
    content: '';
    flex: 1;
    border-bottom: 1px solid ${palette.neutral.white};
    margin-right: 1rem;
    margin-left: 2.86rem;
  }
  ::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid ${palette.neutral.white};
    margin-left: 1rem;
    margin-right: 2.86rem;
  }
`

export const LoginPageContent = styled.div.attrs({
  gridColumn: '5 / span 4',
})`
  ${grid}
  align-self: center;
  text-align: center;
  min-width: 500px;
  padding-top: 50px;
  padding-bottom: 50px;
  color: white;
`

export const TrusteroLogo = styled(TrusteroLogoFullSVG)`
  fill: currentColor;
  * {
    fill: currentColor;
  }
`

export const EmailInputContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  margin-top: 1rem;
`

export const StyledEmailInput = styled.input`
  width: 100%;
  padding: 1.25rem 1rem;
  border: 1px solid ${palette.neutral[100]};
  border-radius: 12px;
  background: white;
  font-size: 1rem;
  outline: none;
  transition: all 0.2s;
  color: ${palette.neutral[900]};

  &::placeholder {
    color: ${palette.neutral[400]};
  }

  &:focus {
    border-color: #4a90e2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`

export const EmailActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;
`

export const StyledStartFlow = styled(StartFlow)`
  width: 100%;
`

export const StyledLoginButton = styled(StandardButton).attrs({
  variant: StandardButtonVariant.PRIMARY,
  buttonSize: StandardButtonSize.LARGE,
})`
  width: 100%;
  position: relative;
  background-color: white;

  &&,
  &&:visited {
    color: ${themeGet('colors.text.default')};
  }

  &:hover,
  &:active {
    color: white;
  }
`

export const LoginErrors = styled.p.attrs<{ show: boolean }>((props) => {
  return {
    mt: 'l',
    p: 'l',
    bg: props.show ? 'bg.status.error' : 'transparent',
    color: 'fill.status.error.base',
  }
})<{ show: boolean }>`
  ${compose(space, color)}
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  border-radius: 6px;
`

export const StyledRedirect = styled.h6`
  margin-bottom: 2em;
  color: ${palette.neutral.white};
`

export const NewLoginPageContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  background-image: url(${backgroundSVG});
  background-size: cover;
  background-position: center;
  overflow: auto;

  @media (max-width: 1400px) {
    flex-direction: column;
    height: 100%;
    min-height: 100vh;
  }

  @media (max-width: 1024px) and (max-height: 500px) {
    flex-direction: row;
  }
`

export const BrandSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: 10%;
  position: relative;

  @media (max-width: 1400px) {
    flex: none;
    padding: 4rem 2rem 2rem;
    align-items: center;
    text-align: center;
  }

  @media (max-width: 1024px) and (max-height: 500px) {
    flex: 1;
    padding: 1rem;
    justify-content: flex-start;
    align-items: center;
    padding-top: 2rem;
  }
`

export const BrandLogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 1024px) {
    align-items: center;
  }

  @media (max-height: 500px) {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
  }
`

export const BrandLogo = styled.div`
  font-size: 4rem;
  font-weight: 700;
  color: ${palette.neutral[900]};
  display: flex;
  align-items: center;
  gap: 0.5rem;

  span {
    color: #4a90e2;
  }

  @media (max-width: 1024px) {
    font-size: 3rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`

export const BrandLink = styled(PageAnchor)`
  font-size: 1.25rem;
  color: ${palette.neutral[900]};
  text-decoration: none;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`

export const LoginCardContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px;

  @media (max-width: 1024px) {
    flex: none;
    padding: 2rem 2rem 4rem;
    width: 100%;
    box-sizing: border-box;
  }

  @media (max-width: 1024px) and (max-height: 500px) {
    flex: 1;
    padding: 2rem;
    width: auto;
    justify-content: flex-start;
  }
`

export const LoginCard = styled.div`
  background: white;
  padding: 3rem;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 700px;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  height: fit-content;
  overflow: auto;

  @media (max-width: 1024px) {
    padding: 2.5rem;
    max-width: 600px;
    height: 100%;
  }

  @media (max-width: 480px), (max-height: 500px) {
    padding: 1.5rem;
    height: 100%;
  }
`

export const LoginCardHeader = styled.h2`
  font-size: 1.25rem;
  font-weight: 500;
  color: ${palette.neutral[900]};
  margin-bottom: 2rem;
`

export const LoginButtonsGrid = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: ${(props) => `repeat(${props.columns || 2}, 1fr)`};
  gap: 1rem;
`

export const NewLoginButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  border-radius: 12px;
  border: none;
  background: ${palette.neutral[30]};
  cursor: pointer;
  transition: all 0.2s;
  gap: 0.75rem;
  min-height: 120px;
  transition: all 0.2s;

  &:hover {
    background: ${palette.neutral[50]};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }

  span {
    font-size: 0.875rem;
    font-weight: 500;
    color: ${palette.neutral[700]};
  }

  @media (max-width: 480px) {
    padding: 1rem 0.5rem;
    min-height: 100px;
    gap: 0.5rem;

    span {
      font-size: 0.75rem;
    }
  }
`

export const NewLoginIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  width: 100%;

  svg {
    max-height: 40px;
    max-width: 40px;
  }

  @media (max-width: 480px) {
    height: 32px;
    svg {
      max-height: 32px;
      max-width: 32px;
    }
  }
`

export const DividerRow = styled.div`
  display: flex;
  align-items: center;
  margin: 2rem 0;
  color: ${palette.neutral[100]};
  font-size: 0.875rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${palette.neutral[100]};
  }

  span {
    margin: 0 1rem;
  }
`

export const AdditionalSignInButton = styled.button`
  background: none;
  border: none;
  color: #4a90e2;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  margin-top: 1rem;
  align-self: center;

  &:hover {
    text-decoration: underline;
  }
`

export const AccessLinkButton = styled.button`
  width: 100%;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-weight: 500;
  background-color: ${palette.neutral[30]};
  color: ${palette.neutral[900]} !important;
  border: none;
  transition: all 0.2s;
  cursor: pointer;
  padding: 8px 16px;

  &:hover {
    background-color: ${palette.neutral[50]};
  }
`

export const AILogo = styled.h1`
  font-weight: 600;
  font-size: 120px;
  background: -webkit-linear-gradient(-20deg, #10b7d8, #7b52d8, #c921ad);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 1024px) {
    font-size: 80px;
  }

  @media (max-width: 480px), (max-height: 500px) {
    font-size: 60px;
  }

  @media (max-height: 400px) {
    font-size: 32px;
  }
`

export const TrusteroLogoBig = styled(TrusteroLogoFullSVG)`
  width: 464px;
  height: 102.1px;
  fill: currentColor;
  * {
    fill: currentColor;
  }

  @media (max-width: 1024px) {
    width: 300px;
    height: auto;
  }

  @media (max-width: 480px), (max-height: 500px) {
    width: 200px;
  }

  @media (max-height: 400px) {
    width: 120px;
  }
`
